// Session-level record of which notifications this browser tab has already
// accounted for. Deliberately MODULE state, not a component ref:
//
//   - `AppLayout` (and therefore the notifications provider) remounts on every
//     page navigation, so a ref would reset and re-toast the whole inbox;
//   - the bell is mounted twice (desktop top bar + mobile header), so per-
//     instance state would toast twice for one notification.
//
// It resets on a full page reload, which is exactly right: the first load after
// a reload re-seeds and stays silent, so a refresh never replays old toasts.

let seeded = false;
const seen = new Set<string>();

/**
 * Record the ids currently known to the client and return only the ones that
 * are genuinely new.
 *
 * The FIRST call seeds silently and returns nothing — those are the
 * notifications that already existed when the tab opened, and toasting them
 * would spam the user with history on every page load.
 */
export const observeNotificationIds = (ids: readonly string[]): string[] => {
  if (!seeded) {
    seeded = true;
    for (const id of ids) seen.add(id);
    return [];
  }

  const fresh: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    fresh.push(id);
  }
  return fresh;
};

/** Test seam — production code never calls this. */
export const resetNotificationToastRegistry = (): void => {
  seeded = false;
  seen.clear();
};
